import { SafeError, SafeType } from '@benzinga/safe-await';
import { ScannerConfig } from '@benzinga/scanner-config-manager';
import { ScannerProtos } from './scanner_protos';
import { isUnboundedFilter } from './filterUtils';
import { DataField, FilterObject, QuotesV3FieldsManager } from '@benzinga/quotes-v3-fields-manager';
import { Session } from '@benzinga/session';

/**
 * Config fields that affect the scanner wire query / transport mode.
 * Strips tags, name, uuid, notifications, etc. so UI-only changes (e.g. adding a
 * GPT prompt tag) do not create a new feed/socket.
 */
export const toScannerFeedConfig = (config: ScannerConfig): ScannerConfig => ({
  filters: config.filters,
  limit: config.limit,
  refreshInterval: config.refreshInterval,
  sortDir: config.sortDir,
  sortField: config.sortField,
  source: config.source,
  tableParameters: {
    // Keep only visible column ids for feed identity; normalize UI-only grid state away.
    bzColumnState: [],
    columns: [...new Set(config.tableParameters.columns.filter(col => !col.hide).map(col => col.colId))]
      .sort()
      .map(colId => ({ colId })),
    columnsResized: false,
    filter: {},
  },
  tags: [],
});

export const configToQuery = (config: ScannerConfig, session: Session): SafeType<ScannerProtos.Query> => {
  const defs = session.getManager(QuotesV3FieldsManager).getCachedScannerDefs();
  if (defs) {
    const filtersAsString = toQueryFilters(config.filters).join(';');
    if (process.env.NODE_ENV === 'development') {
      const industryFilter = config.filters.find(f => f.field === 'gicsIndustryName');
      if (industryFilter) {
        console.log('[Scanner] Industry filter:', {
          field: industryFilter.field,
          filterString: filtersAsString,
          operator: industryFilter.operator,
          parameters: industryFilter.parameters,
        });
      }
    }
    return {
      ok: ScannerProtos.Query.create({
        fields: config.tableParameters.columns
          .map(field => field.colId)
          .filter(name => defs.some(s => s.name === name)),
        filtersAsString,
        limit: config.limit,
        sortDir: config.sortDir,
        sortField: defs.some(s => s.name === config.sortField) ? config.sortField : undefined,
      }),
    };
  } else {
    return { err: new SafeError('cannot convert request to Query before Scanner Defs are received', 'scanner') };
  }
};

const toQueryFilters = (filters: FilterObject[]): string[] => {
  return filters.map(f => toQueryFilter(f)).filter<string>((s): s is string => s !== undefined);
};

const getName = (filter: FilterObject) => {
  if (filter.field === 'watchlist') {
    return 'symbol';
  }
  return filter.field;
};

const toQueryFilter = (filter: FilterObject): string | undefined => {
  if (isUnboundedFilter(filter)) {
    return undefined;
  }
  if (filter.operator === 'lt' || filter.operator === 'gt') {
    return `${getName(filter)}_${filter.operator}_${filter.parameters[0]}`;
  }
  return `${getName(filter)}_${filter.operator}_${filter.parameters.join(',')}`;
};

export const filterSearchSelected = (selectedFilters, session, searchText) =>
  selectedFilters
    .map(filter => ({
      df: session.getManager(QuotesV3FieldsManager).getScannerDefFromId(filter.field),
      filter,
    }))
    .filter(
      (item): item is { df: DataField; filter: FilterObject } =>
        !!item.df?.label?.toLowerCase().includes(searchText.toLowerCase() ?? '') ||
        !!item.df?.name?.toLowerCase().includes(searchText.toLowerCase() ?? '') ||
        !!item.df?.labelShort?.toLowerCase().includes(searchText.toLowerCase() ?? ''),
    );

export const searchResults = (dataFields, searchText) => {
  if (!searchText || searchText.length === 0) {
    return dataFields;
  }
  const searchTextLower = searchText.toLowerCase();
  return dataFields.filter(field => {
    const label = field.label?.toLowerCase() ?? '';
    const name = field.name?.toLowerCase() ?? '';
    const labelShort = field.labelShort?.toLowerCase() ?? '';
    return label.includes(searchTextLower) || name.includes(searchTextLower) || labelShort.includes(searchTextLower);
  });
};
